// Import polyfills first
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Analytics } from '@vercel/analytics/react'
import App from './App'
import './index.css'
import reportWebVitals from './reportWebVitals'
import {
  registerServiceWorker,
  initializeAssetErrorHandling,
  clearAssetReloadFlag,
  cleanLegacyUrlParams
} from './utils/sw/pwaUtils'
import { enableConsoleSupression } from './utils/consoleUtils'
import { trackReloadLoop } from './utils/reloadLoopTracker'

// Deploy test: verify new version loads without stale cache
console.log(`[Deploy Test] Version ${__APP_VERSION__} loaded successfully`)

// CRITICAL: Clean legacy URL parameters FIRST to break infinite reload loops
// Old cached JavaScript (from stale service workers) may have added ?v= or ?cache_bust=
// This must happen before any other code that might trigger a reload
cleanLegacyUrlParams()

// Track reload loops early - sets Sentry/PostHog context before React renders
trackReloadLoop()

// Enable console warning suppression for known third-party library issues
enableConsoleSupression({
  suppressRechartsWarnings: true, // Suppress Recharts defaultProps warnings
  suppressFirebaseWarnings: false, // Set to true if you want to suppress Firebase 400 errors
  customFilters: [
    // Add any custom filters here
    // Example: (message) => message.includes('some warning to suppress')
  ]
})

// Initialize unified asset error handling FIRST (before any other code that might fail)
// This sets up global error listeners for chunk/CSS/module loading failures
initializeAssetErrorHandling()

// PWA Service Worker - controlled by ENABLE_PWA env var at build time (default: disabled)
if (!import.meta.env.DEV) {
  if (__PWA_ENABLED__) {
    registerServiceWorker()
  } else {
    // PWA disabled - use self-destroying SW to force reload all tabs with stale code
    // This handles the case where old tabs have registered SWs that keep serving cached JS
    if ('serviceWorker' in navigator) {
      // Check if there are any existing service workers
      navigator.serviceWorker.getRegistrations().then(async (registrations) => {
        if (registrations.length > 0) {
          // Old SWs exist - register the self-destroying SW to take over and force reload all tabs
          // sw.js clears caches, unregisters itself, and reloads all clients
          console.log('[Index] Found', registrations.length, 'existing SW(s), registering self-destroying SW...')
          try {
            await navigator.serviceWorker.register('/sw.js')
            console.log('[Index] Self-destroying SW registered, it will clean up and reload...')
          } catch (err) {
            console.error('[Index] Failed to register SW:', err)
            // Fallback: just unregister and clear caches
            registrations.forEach((r) => r.unregister())
            caches?.keys().then((names) => names.forEach((n) => caches.delete(n)))
          }
        } else {
          // No existing SWs, just clear any leftover caches
          caches?.keys().then((names) => names.forEach((n) => caches.delete(n)))
        }
      })
    }
  }
}

// Only render Vercel Analytics on Vercel-hosted domains
// Firebase Hosting doesn't have /_vercel/insights/script.js endpoint
const isVercelHosted =
  window.location.hostname.endsWith('.vercel.app') ||
  window.location.hostname === 'app.tryloop.ai'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
    {isVercelHosted && <Analytics />}
  </React.StrictMode>
)

// App loaded successfully - clear asset error reload flag
// This ensures the flag doesn't persist incorrectly
clearAssetReloadFlag()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

// Skew Protection whitespace test - safe to remove
